import { computed, watch, nextTick, ref } from "vue";
import { useForm } from "@tanstack/vue-form";
import { useDateUtils } from "./useDateUtils";
import { useMasterDataHelpers } from "./useMasterDataHelpers";
// Durations must match CreateWalkInRoomBookingFilter
import { snapToAllowedWalkInDuration } from "../utils/walkInSelection.js";
import {
	TITLE_MAX_LENGTH,
	DESCRIPTION_MAX_LENGTH,
	getFieldValidationMessage,
} from "../../services/validationMessages.js";

export function useBookingForm(props, emit) {
	const { formatDateForInput, formatTimeForInput } = useDateUtils();
	const masterData = useMasterDataHelpers();

	// Reactive room ID for modal title
	const currentRoomId = ref(null);

	// Walk-in only: length of the dragged selection when it was longer than the allowed
	// maximum, otherwise null. Drives the hint below the end-time chips.
	const walkInShortenedFromMinutes = ref(null);

	// Clean default values for form resets
	const getCleanDefaultValues = () => ({
		title: "",
		startDate: "",
		startTime: "",
		endDate: "",
		endTime: "",
		description: "",
		createForOther: false,
		selectedPersonId: "",
		selectedPersonName: "",
		selectedPersonIdForDisplay: "",
		personType: "student",
		rrulePattern: "",
		cancelledDates: [],
		roomId: null,
		bookingTypeId:
			// For students in create mode, use default booking type
			(props.mode === "create" &&
			!props.booking &&
			masterData.userRole.value === "student")
				? masterData.defaultBookingType.value?.id || null
				: null,
		ignoreOpeningHours: false,
		bookWithoutFunds: false,
		sendMailNotification: true,
		durationMinutes: 60,
	});

	// Create TanStack form with default values
	const form = useForm({
		defaultValues: getCleanDefaultValues(),
		onSubmit: async ({ value }) => {
			const bookingData = createBookingDataFromForm(value);
			emitFormAction(bookingData);
		},
	});

	// Form change detection - reactive ref updated via Subscribe component
	const hasFormChanged = ref(true); // Default to true for create mode

	// Helper function to calculate form changes using TanStack Form's fieldMeta
	const calculateFormChanges = (fieldMeta) => {
		// For create mode, we always allow submission
		if (props.mode === "create") {
			return true;
		}

		try {
			if (!fieldMeta) {
				return true; // If no meta available, allow submission
			}

			// Check each field for non-persistent dirty state
			// A field is "changed" if it's dirty AND not at default value
			const hasChanges = Object.keys(fieldMeta).some(fieldName => {
				const meta = fieldMeta[fieldName];
				// Skip the email notification field as it shouldn't prevent saving
				if (fieldName === 'sendMailNotification') {
					return false;
				}
				// Non-persistent dirty: field is dirty but value is not default
				return meta?.isDirty && !meta?.isDefaultValue;
			});


			return hasChanges;
		} catch (error) {
			console.warn("Error checking TanStack dirty state:", error);
			return true; // If error, allow submission
		}
	};

	// Method to update hasFormChanged from Subscribe component
	const updateFormChanged = (fieldMeta) => {
		hasFormChanged.value = calculateFormChanges(fieldMeta);
	};

	// Helper function to reset form with current values to clear dirty state
	const resetFormToBaseline = () => {
		nextTick(() => {
			try {
				const currentValues = form.state.values;
				form.reset(currentValues);
			} catch (error) {
				console.warn("Failed to reset form to baseline:", error);
			}
		});
	};

	// Computed properties
	const modalTitle = computed(() => {
		if (props.isWalkIn) {
			return "Sofortbuchung";
		}

		const baseTitle =
			{
				edit: "Buchung bearbeiten",
				view: "Buchungsdetails",
				"edit-series": "Serie bearbeiten",
				"edit-instance": "Instanz bearbeiten",
				conflict: "Konflikt lösen",
			}[props.mode] || "Buchung";

		// Use reactive room ID
		const roomId = currentRoomId.value;

		if (roomId && props.mode === "create") {
			const room = masterData.getRoomById.value(roomId);
			if (room) {
				const roomText = room.building
					? `${room.building.short}: ${room.doorLabel}`
					: room.doorLabel;
				return `Neue Buchung in Raum ${roomText} erstellen`;
			}
		}

		return baseTitle;
	});

	const canEdit = computed(() => {
		return props.booking?.permissions?.canUpdate || false;
	});

	const canDelete = computed(() => {
		return props.booking?.permissions?.canDelete || false;
	});

	// Validation functions for form fields - same wording the server-side rules map to
	const titleValidator = {
		onChange: ({ value }) => {
			if (value && value.length > TITLE_MAX_LENGTH) {
				return getFieldValidationMessage("title", "stringLengthTooLong");
			}
			return undefined;
		},
	};

	const descriptionValidator = {
		onChange: ({ value }) => {
			if (value && value.length > DESCRIPTION_MAX_LENGTH) {
				return getFieldValidationMessage("description", "stringLengthTooLong");
			}
			return undefined;
		},
	};

	// Booking pattern handling - simplified with computed
	const bookingPattern = computed(() => {
		if (props.mode === "edit-instance") {
			return "single";
		} else if (props.mode === "edit-series") {
			return "series";
		}
		// Check if form has rrule pattern to determine type
		try {
			const rrulePattern = form.getFieldValue?.("rrulePattern");
			// Consider empty string, null, or undefined as single booking
			const result =
				rrulePattern &&
				typeof rrulePattern === "string" &&
				rrulePattern.trim() !== ""
					? "series"
					: "single";
			return result;
		} catch (error) {
			return "single";
		}
	});

	// Form methods
	function resetForm() {
		try {
			// Reset to clean default values
			form.reset(getCleanDefaultValues());
		} catch (error) {
			console.warn("Form reset failed:", error);
		}
	}


	function setFormValue(fieldName, value) {
		try {
			if (form.setFieldValue) {
				form.setFieldValue(fieldName, value);
			}
		} catch (error) {
			console.warn(`Failed to set field ${fieldName}:`, error);
		}
	}

	function createBookingDataFromForm(formValues) {
		// Walk-in bookings only send roomId + durationMinutes plus the optional title and
		// description - start/end time, booking type and person are all fixed/computed
		// server-side.
		if (props.isWalkIn) {
			return {
				roomId: formValues.roomId,
				durationMinutes: formValues.durationMinutes,
				title:
					formValues.title && formValues.title.trim() !== ""
						? formValues.title.trim()
						: null,
				description:
					formValues.description && formValues.description.trim() !== ""
						? formValues.description?.trim()
						: null,
			};
		}

		// Create Date objects from form inputs (these will be in user's local timezone)
		const startTime = new Date(
			`${formValues.startDate}T${formValues.startTime}`
		);
		const endTime = new Date(`${formValues.endDate}T${formValues.endTime}`);

		// For series updates, preserve original series start date but update time if changed
		let finalStartTime = startTime;
		let finalEndTime = endTime;

		if (props.mode === "edit-series" && props.booking?.series) {
			// For series updates, we want to preserve the original series start date
			// but allow time changes. The series object contains the original startTime and endTime.

			const originalSeriesStart = new Date(props.booking.series.startTime);
			const originalSeriesEnd = new Date(props.booking.series.endTime);

			// Check if only the time components have changed
			const originalStartTime = formatTimeForInput(originalSeriesStart);
			const originalEndTime = formatTimeForInput(originalSeriesEnd);
			const currentStartTime = formValues.startTime;
			const currentEndTime = formValues.endTime;

			// Always use the original series start date, but allow time updates
			const originalStartDate = formatDateForInput(originalSeriesStart);

			if (
				originalStartTime !== currentStartTime ||
				originalEndTime !== currentEndTime
			) {
				// Time has changed - use original series date with new times
				finalStartTime = new Date(`${originalStartDate}T${currentStartTime}`);
				finalEndTime = new Date(`${originalStartDate}T${currentEndTime}`);
			} else {
				// Time hasn't changed - preserve original series times completely
				finalStartTime = originalSeriesStart;
				finalEndTime = originalSeriesEnd;
			}
		}

		const bookingData = {
			title:
				formValues.title && formValues.title.trim() !== ""
					? formValues.title.trim()
					: null,
			startTime: finalStartTime.toISOString(), // Send as UTC ISO string
			endTime: finalEndTime.toISOString(), // Send as UTC ISO string
			description:
				formValues.description && formValues.description.trim() !== ""
					? formValues.description.trim()
					: null,
			ignoreOpeningHours: Boolean(formValues.ignoreOpeningHours),
		};

		// Add roomId if available
		if (formValues.roomId) {
			bookingData.roomId = formValues.roomId;
		}

		// Add booking type ID - ensure it's always included when available
		if (formValues.bookingTypeId) {
			bookingData.bookingTypeId = formValues.bookingTypeId;
		} else if (
			masterData.userRole.value === "student" &&
			masterData.defaultBookingType.value?.id
		) {
			// For students, use default booking type as fallback
			bookingData.bookingTypeId = masterData.defaultBookingType.value.id;
		}

		// Handle person associations
		if (formValues.createForOther) {
			if (formValues.personType === "unpersonalized") {
				// Explicitly set both to null for unpersonalized bookings
				bookingData.studentId = null;
				bookingData.employeeId = null;
			} else if (formValues.selectedPersonId) {
				if (formValues.personType === "student") {
					bookingData.studentId = formValues.selectedPersonId;
					bookingData.employeeId = null;
					// When booking for a student, ensure we have the default booking type
					if (
						!bookingData.bookingTypeId &&
						masterData.defaultBookingType.value?.id
					) {
						bookingData.bookingTypeId = masterData.defaultBookingType.value.id;
					}
				} else if (formValues.personType === "employee") {
					bookingData.employeeId = formValues.selectedPersonId;
					bookingData.studentId = null;
				}
			}
		} else {
			// User is booking for themselves - use their own ID based on role
			if (masterData.masterData.value?.userInfo?.student?.id) {
				bookingData.studentId = masterData.masterData.value.userInfo.student.id;
				bookingData.employeeId = null;
			} else if (masterData.masterData.value?.userInfo?.employee?.id) {
				bookingData.employeeId =
					masterData.masterData.value.userInfo.employee.id;
				bookingData.studentId = null;
			} else {
				bookingData.studentId = null;
				bookingData.employeeId = null;
			}
		}

		// Add withoutTransaction only for student bookings (not for employees or unpersonalized)
		const isStudentBooking =
			(formValues.createForOther && formValues.personType === "student") ||
			(!formValues.createForOther && masterData.userRole.value === "student");

		if (isStudentBooking) {
			bookingData.withoutTransaction = Boolean(formValues.bookWithoutFunds);
		}

		// Add series-specific data if series booking or series edit
		if (
			props.mode === "edit-series" ||
			(formValues.rrulePattern && formValues.rrulePattern.trim())
		) {
			bookingData.pattern = formValues.rrulePattern;
			bookingData.cancellations = formValues.cancelledDates || [];
		}

		// Add display fields for "booking for others" preservation during error flows
		if (formValues.selectedPersonName) {
			bookingData.selectedPersonName = formValues.selectedPersonName;
		}
		if (formValues.selectedPersonIdForDisplay) {
			bookingData.selectedPersonIdForDisplay =
				formValues.selectedPersonIdForDisplay;
		}

		// Add personType for preservation during error flows
		if (formValues.personType) {
			bookingData.personType = formValues.personType;
		}

		// Add booking without funds field for preservation during error flows
		bookingData.bookWithoutFunds = Boolean(formValues.bookWithoutFunds);

		// Add email notification preference
		bookingData.sendMailNotification = Boolean(formValues.sendMailNotification);

		return bookingData;
	}

	function emitFormAction(bookingData) {
		if (props.mode === "create" && props.isWalkIn) {
			emit("save-walkin", bookingData);
		} else if (props.mode === "create") {
			// Determine endpoint based on whether booking data contains series pattern
			const endpoint =
				bookingData.pattern && bookingData.pattern.trim()
					? "series/create"
					: "instance/create";
			emit("save", { endpoint, data: bookingData });
		} else if (props.mode === "edit") {
			emit("update", props.booking.id, bookingData);
		} else if (props.mode === "edit-series") {
			const seriesId = props.booking.series?.id;
			if (seriesId) {
				emit("update-series", seriesId, bookingData);
			}
		} else if (props.mode === "edit-instance") {
			emit("update-instance", props.booking.id, bookingData);
		}
	}

	function populateFormFromProps(isDragDrop = false) {
		nextTick(() => {
			// Priority order: prefillData > booking > selectedSlot
			if (props.prefillData) {
				populateFormFromPrefillData(props.prefillData, isDragDrop);
			} else if (props.mode === "create" && props.selectedSlot) {
				populateFormFromSlot(props.selectedSlot);
			} else if (
				(props.mode === "edit" ||
					props.mode === "view" ||
					props.mode === "edit-series" ||
					props.mode === "edit-instance") &&
				props.booking
			) {
				populateFormFromBooking(props.booking);
			}
		});
	}

	function populateFormFromSlot(slot) {
		if (!slot) return;

		const start = new Date(slot.start);
		const end = new Date(slot.end);

		// Update form values individually
		setFormValue("startDate", formatDateForInput(start));
		setFormValue("startTime", formatTimeForInput(start));
		setFormValue("endDate", formatDateForInput(end));
		setFormValue("endTime", formatTimeForInput(end));

		if (slot.roomId) {
			setFormValue("roomId", slot.roomId);
		}

		if (props.isWalkIn) {
			const draggedMinutes = Math.round((end - start) / 60000);
			const snappedMinutes = snapToAllowedWalkInDuration(draggedMinutes);
			// Remember a shortened selection so the form can say so instead of silently
			// snapping the drag down to the maximum walk-in duration.
			walkInShortenedFromMinutes.value =
				snappedMinutes < draggedMinutes ? draggedMinutes : null;
			setFormValue("durationMinutes", snappedMinutes);
		}

		resetFormToBaseline();
	}

	function populateFormFromBooking(booking) {
		if (!booking) return;

		const start = new Date(booking.startTime);
		const end = new Date(booking.endTime);

		// Check if this is an unpersonalized booking (both IDs are null/undefined)
		const isUnpersonalized = !booking.student?.id && !booking.employee?.id;

		// Check if the booking is for the current user
		const currentUserStudentId =
			masterData.masterData.value?.userInfo?.student?.id;
		const currentUserEmployeeId =
			masterData.masterData.value?.userInfo?.employee?.id;

		const isForCurrentUser =
			(booking.student?.id && booking.student.id === currentUserStudentId) ||
			(booking.employee?.id && booking.employee.id === currentUserEmployeeId);

		// Only set createForOther to true if:
		// 1. It's an unpersonalized booking (for admins)
		// 2. It's a booking for someone else (not the current user)
		const isForOther =
			!isForCurrentUser && (booking.student?.id || booking.employee?.id);

		// Build complete form values object
		const formValues = {
			title: booking.title ?? "",
			startDate: formatDateForInput(start),
			startTime: formatTimeForInput(start),
			endDate: formatDateForInput(end),
			endTime: formatTimeForInput(end),
			description: booking.description ?? "",
			ignoreOpeningHours: booking.ignoreOpeningHours || false,
			roomId: booking.roomId || null,
			bookingTypeId: booking.bookingTypeId || null,
			rrulePattern: booking.series?.pattern || "",
			cancelledDates: booking.series?.cancellations || [],
			bookWithoutFunds: booking.withoutTransaction ?? false,
			sendMailNotification: booking.sendMailNotification ?? true,
		};

		// Handle person-related fields based on booking type
		if (isUnpersonalized && masterData.canManageOtherBookings.value) {
			// Unpersonalized booking
			Object.assign(formValues, {
				createForOther: true,
				personType: "unpersonalized",
				selectedPersonId: "",
				selectedPersonName: "",
				selectedPersonIdForDisplay: "",
			});
		} else if (isForOther) {
			// Booking for someone else
			Object.assign(formValues, {
				createForOther: true,
				selectedPersonId: booking.student?.id || booking.employee?.id || "",
				personType: booking.student?.id ? "student" : "employee",
				selectedPersonName: booking.student?.displayName || booking.employee?.displayName || "",
				selectedPersonIdForDisplay: booking.student?.email || booking.employee?.email || "",
			});
		} else {
			// Booking for current user or no person association
			Object.assign(formValues, {
				createForOther: false,
				selectedPersonId: "",
				personType: "student",
				selectedPersonName: "",
				selectedPersonIdForDisplay: "",
			});
		}

		// Reset form with all values at once
		form.reset(formValues);
	}

	function populateFormFromPrefillData(prefillData, isDragDrop = false) {
		if (!prefillData) return;

		// Always handle dates properly if they are ISO strings
		if (
			typeof prefillData.startTime === "string" &&
			prefillData.startTime.includes("T")
		) {
			const start = new Date(prefillData.startTime);
			const end = new Date(prefillData.endTime);
			setFormValue("startDate", formatDateForInput(start));
			setFormValue("startTime", formatTimeForInput(start));
			setFormValue("endDate", formatDateForInput(end));
			setFormValue("endTime", formatTimeForInput(end));
		}

		// Handle title and description
		if (prefillData.title !== undefined) {
			setFormValue("title", prefillData.title ?? "");
		}
		if (prefillData.description !== undefined) {
			setFormValue("description", prefillData.description ?? "");
		}

		// Handle room
		if (prefillData.roomId !== undefined) {
			setFormValue("roomId", prefillData.roomId);
		}

		// Walk-in data carries a duration instead of an end time (e.g. when returning to the
		// Sofortbuchung form after backing out of an error step)
		if (prefillData.durationMinutes !== undefined) {
			setFormValue("durationMinutes", prefillData.durationMinutes);
		}

		// Handle booking type
		if (prefillData.bookingTypeId !== undefined) {
			setFormValue("bookingTypeId", prefillData.bookingTypeId);
		}

		// Handle person data - check multiple sources
		let studentId = null;
		let employeeId = null;

		// Direct IDs (from form data or enhanced drag data)
		if (prefillData.studentId !== undefined) {
			studentId = prefillData.studentId;
		} else if (prefillData.student?.id !== undefined) {
			studentId = prefillData.student.id;
		}

		if (prefillData.employeeId !== undefined) {
			employeeId = prefillData.employeeId;
		} else if (prefillData.employee?.id !== undefined) {
			employeeId = prefillData.employee.id;
		}

		// Check if the person selection is for the current user
		const currentUserStudentId =
			masterData.masterData.value?.userInfo?.student?.id;
		const currentUserEmployeeId =
			masterData.masterData.value?.userInfo?.employee?.id;

		const isCurrentUser =
			(studentId && studentId === currentUserStudentId) ||
			(employeeId && employeeId === currentUserEmployeeId);

		// Handle person type - check if explicitly set in prefill data first
		let personType = "student"; // default
		if (prefillData.personType !== undefined) {
			personType = prefillData.personType;
		} else if (studentId) {
			personType = "student";
		} else if (employeeId) {
			personType = "employee";
		}

		// Determine if this is for another person or unpersonalized
		const hasPersonSelection = !!(studentId || employeeId);
		const isUnpersonalized = personType === "unpersonalized";

		const explicitCreateForOther =
			typeof prefillData.createForOther === "boolean"
				? prefillData.createForOther
				: null;

		const shouldCreateForOther =
			null !== explicitCreateForOther
				? explicitCreateForOther
				: hasPersonSelection || isUnpersonalized;

		setFormValue("createForOther", shouldCreateForOther);

		setFormValue(
			"selectedPersonId",
			shouldCreateForOther && !isUnpersonalized ? (studentId || employeeId || "") : ""
		);
		setFormValue("personType", personType);

		// Handle person name and email fields for display
		if (shouldCreateForOther && !isUnpersonalized) {
			// Get person name from prefill data
			const personName = studentId
				? prefillData.student?.displayName ||
				  prefillData.selectedPersonName ||
				  ""
				: prefillData.employee?.displayName ||
				  prefillData.selectedPersonName ||
				  "";
			const personEmail = studentId
				? prefillData.student?.email ||
				  prefillData.selectedPersonIdForDisplay ||
				  ""
				: prefillData.employee?.email ||
				  prefillData.selectedPersonIdForDisplay ||
				  "";

			setFormValue("selectedPersonName", personName);
			setFormValue("selectedPersonIdForDisplay", personEmail);
		} else {
			// Clear person fields for unpersonalized or not-for-other bookings
			setFormValue("selectedPersonName", "");
			setFormValue("selectedPersonIdForDisplay", "");
		}

		// Handle series data - check multiple sources
		let pattern = "";
		let cancellations = [];

		if (prefillData.pattern !== undefined) {
			pattern = prefillData.pattern;
		} else if (prefillData.rrulePattern !== undefined) {
			pattern = prefillData.rrulePattern;
		} else if (prefillData.series?.pattern !== undefined) {
			pattern = prefillData.series.pattern;
		}

		if (prefillData.cancellations !== undefined) {
			cancellations = prefillData.cancellations;
		} else if (prefillData.cancelledDates !== undefined) {
			cancellations = prefillData.cancelledDates;
		} else if (prefillData.series?.cancellations !== undefined) {
			cancellations = prefillData.series.cancellations;
		}

		setFormValue("rrulePattern", pattern);
		setFormValue("cancelledDates", cancellations);

		// Handle other fields
		if (prefillData.ignoreOpeningHours !== undefined) {
			setFormValue("ignoreOpeningHours", prefillData.ignoreOpeningHours);
		}

		// Handle booking without funds checkbox - check both possible field names
		if (prefillData.bookWithoutFunds !== undefined) {
			setFormValue("bookWithoutFunds", prefillData.bookWithoutFunds);
		} else if (prefillData.withoutTransaction !== undefined) {
			setFormValue("bookWithoutFunds", prefillData.withoutTransaction);
		}

		// Handle email notification preference
		if (prefillData.sendMailNotification !== undefined) {
			setFormValue("sendMailNotification", prefillData.sendMailNotification);
		}

		// Only reset to baseline if this is not a drag/drop operation
		// For drag/drop, we want to maintain dirty state to enable submit button
		if (!isDragDrop) {
			resetFormToBaseline();
		}
	}

	// Watch for student selection changes to clear series pattern
	watch(
		() => {
			try {
				const createForOther = form.getFieldValue?.("createForOther");
				const personType = form.getFieldValue?.("personType");
				const selectedPersonId = form.getFieldValue?.("selectedPersonId");
				return { createForOther, personType, selectedPersonId };
			} catch (error) {
				return { createForOther: false, personType: "", selectedPersonId: "" };
			}
		},
		(newValues) => {
			// If a student is selected, clear the rrule pattern to force one-time booking
			if (
				newValues.createForOther &&
				newValues.personType === "student" &&
				newValues.selectedPersonId
			) {
				// Clear series-related fields similar to BookingPatternSelector's setBookingPattern('single')
				setFormValue("rrulePattern", "");
				setFormValue("cancelledDates", []);

				// Also ensure booking type is set for student
				if (
					!form.getFieldValue?.("bookingTypeId") &&
					masterData.defaultBookingType.value?.id
				) {
					setFormValue("bookingTypeId", masterData.defaultBookingType.value.id);
				}
			}
		},
		{ deep: true }
	);

	// Function to update current room ID
	const updateCurrentRoomId = () => {
		let roomId = null;
		try {
			roomId =
				form.getFieldValue?.("roomId") ||
				props.booking?.roomId ||
				props.selectedSlot?.roomId;
		} catch (error) {
			roomId = props.booking?.roomId || props.selectedSlot?.roomId;
		}
		currentRoomId.value = roomId;
	};

	// Watch for form changes to update room ID
	watch(
		() => {
			try {
				return form.getFieldValue?.("roomId");
			} catch (error) {
				return null;
			}
		},
		updateCurrentRoomId,
		{ immediate: true }
	);

	// Watch for props changes
	watch(
		() => [props.booking?.roomId, props.selectedSlot?.roomId],
		updateCurrentRoomId,
		{ immediate: true }
	);


	if (props.show) {
		updateCurrentRoomId();
	}


	return {
		form,
		bookingPattern,
		modalTitle,
		canEdit,
		canDelete,
		canCreateSeries: masterData.canCreateSeries.value,
		canBookForOthers: masterData.canBookForOthers.value,
		titleValidator,
		descriptionValidator,
		resetForm,
		populateFormFromSlot,
		populateFormFromBooking,
		populateFormFromPrefillData,
		populateFormFromProps,
		hasFormChanged,
		updateFormChanged,
		walkInShortenedFromMinutes,
	};
}
